import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { TranslateHTML, Translate, withTranslate } from 'core/translate';
import withSaveBuild from '../../../common/saveBuild/SaveBuild';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalFooterButtons,
  ModalFooterPrimaryButton,
  ModalHeader,
  ModalCustomContent,
} from '../../../components/modal/Modal';
import { useModelInfo } from '../../../common/ModelInfoContext';
import { ButtonText, Button } from 'core/controls/button/Button';
import SavedBuildCard from './SavedBuildsCard';
import { getDeepUrl } from '../../../common/urlBuilder';
import { fetchBySavedBuildData } from '../../../api/fetchConfig';
import { computeModelSelectorUrl } from './modelSelectorUrl';

const SavedBuilds = ({
  getAllSavedBuilds,
  removeBuild,
  count,
  translate,
  postalCode,
  config,
  shoppingLinks,
  language,
}) => {
  const [savedBuilds, setSavedBuilds] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const modelInfo = useModelInfo();

  const ANALYTICS_KEY = 'your saved vehicle';
  const fetchSavedBuild = (build) => {
    const { ss, styleId, baseUrl, styleWithoutLang, isEvMode } = build;

    const style = `${language}/${styleWithoutLang}`;

    return fetchBySavedBuildData(styleId, style, ss, {
      postalCode: postalCode,
    })
      .then((config) => {
        const {
          exteriorColorDescription,
          interiorColorDescription,
          transmissionDescription,
          engineDescription,
        } = get(config, 'selections');

        const netPrice = get(config, 'msrp.normalizedNetPriceDisplay');
        const isUnknownPrice = get(config, 'msrp.isUnknownPrice');

        const totalIncludedIncentives = get(
          config,
          'sumary.credits.netPrice.includedTotal.value'
        );
        const netPriceDisclaimer = get(
          config,
          'summary.credits.netPrice.disclaimers.summary',
          translate('byo.page.general.disclaimer.net.price.simple.v3')
        );

        //Localized the following values for the build, for cases where the build was saved
        //in a different language than the current.
        build.driveType = get(config, 'driveType.label');
        build.formattedConfig = isEvMode
          ? ''
          : get(config, 'bodyType.formattedConfig');
        build.trim = get(config, 'selections.trimName');
        build.bodyStyleTitle = get(config, 'style.vehicleInfo.bodyStyleTitle');

        return {
          isUnknownPrice,
          isValid: true,
          ...build,
          exteriorColorDescription,
          interiorColorDescription,
          transmissionDescription,
          engineDescription,
          netPrice,
          totalIncludedIncentives,
          netPriceDisclaimer,
          url: getDeepUrl(baseUrl, config, postalCode),
        };
      })
      .catch(() => {
        removeBuild(build.key);
        return {
          isValid: false,
          ...build,
        };
      });
  };

  const getSavedBuildsWithData = (abortController) => {
    const builds = getAllSavedBuilds();

    setSavedBuilds(builds);

    const buildPromises = builds.map(fetchSavedBuild);
    Promise.all(buildPromises).then((data) => {
      if (!abortController || !abortController.signal.aborted) {
        setSavedBuilds(data);
        setIsLoading(false);
      }
    }),
      () => {
        if (!abortController || !abortController.signal.aborted) {
          setIsLoading(false);
        }
      };
  };

  useEffect(() => {
    const abortController = new AbortController();
    getSavedBuildsWithData(abortController);
    return () => abortController.abort();
  }, []);

  const modelSelectorUrl = computeModelSelectorUrl(modelInfo)(shoppingLinks);

  const header = translate('saved.builds.modal.title.v3');
  const navigate = useNavigate();

  const onClose = () => {
    navigate('..');
  };

  const onRemoveBuild = (key) => {
    removeBuild(key);
    getSavedBuildsWithData();
  };

  const onViewDetails = (build) => {
    if (config.ss !== get(build, 'ss')) {
      navigate('..');
      window.location.href = get(build, 'url');
    } else {
      navigate('../../summary');
    }
  };

  const SavedWithBuilds = ({
    count,
    savedBuilds,
    removeBuild,
    onViewDetails,
    analyticsKey,
  }) => {
    return (
      <div className="saved-builds__container">
        <div className="saved-builds__header-container">
          {count === 1 ? (
            <TranslateHTML
              as="div"
              className="saved-build__count-label"
              value="you.have.saved.one.build.v3"
            />
          ) : (
            <TranslateHTML
              as="div"
              className="saved-build__count-label"
              value="you.have.saved.builds.v3"
              count={count}
            />
          )}
          <TranslateHTML
            className="saved-build__content"
            value="saved.build.content2.v3"
          />{' '}
          <TranslateHTML
            className="saved-build__disclosure"
            value="saved.builds.disclosure.v3"
          />
        </div>
        <div className="gridview__grid-container saved-builds__grid">
          {savedBuilds.map((build) => {
            return (
              <SavedBuildCard
                key={build.key}
                build={build}
                onRemoveBuild={removeBuild}
                postalCode={postalCode}
                currentConfig={config}
                onViewDetails={onViewDetails}
                isLoading={isLoading}
                analyticsKey={analyticsKey}
              />
            );
          })}
        </div>
        <Button
          as="a"
          href={modelSelectorUrl}
          className="saved-builds__start-new-build-button stat-button-link"
          data-dtm={analyticsKey}>
          <ButtonText>
            <Translate value="saved.build.start.new.build.v3" />
          </ButtonText>
        </Button>
      </div>
    );
  };

  const SavedWithNoBuilds = () => {
    const { title } = useModelInfo();

    return (
      <div className="saved-builds__container">
        <div className="saved-builds__header">
          <Translate value="you.have.no.saved.build.v3" />
        </div>
        <TranslateHTML
          className="saved-builds__content saved-builds__content_line"
          value="no.saved.build.content.v3"
          carline={title}
        />
        <TranslateHTML
          className="saved-builds__content saved-builds__content_line"
          value="saved.build.content2.v3"
        />
        <TranslateHTML
          className="saved-builds__content"
          value="saved.builds.disclosure.v3"
        />
      </div>
    );
  };

  return (
    <Modal isOpen={true} contentLabel={header} onRequestClose={onClose}>
      <ModalHeader
        className="saved-builds__modal-header"
        text={header}
        onClose={onClose}
        analyticsKey={ANALYTICS_KEY}
      />
      <ModalCustomContent>
        {({ setIsClosing, onCloseModal }) =>
          count === 0 ? (
            <ModalBody
              className="saved-builds__modal"
              innerClassName="saved-builds__modal-body saved-builds__modal-body--no-build">
              <SavedWithNoBuilds />
            </ModalBody>
          ) : (
            <ModalBody
              className="saved-builds__modal"
              innerClassName="saved-builds__modal-body">
              <SavedWithBuilds
                count={count}
                analyticsKey={ANALYTICS_KEY}
                savedBuilds={savedBuilds}
                removeBuild={onRemoveBuild}
                onViewDetails={(build) => {
                  setIsClosing(
                    true,
                    onCloseModal().then(() => {
                      onViewDetails(build);
                    })
                  );
                }}
              />
            </ModalBody>
          )
        }
      </ModalCustomContent>
      <ModalFooter>
        <ModalFooterButtons classModifier="saved-builds">
          <ModalFooterPrimaryButton
            className="saved-builds__continue-button stat-button-link"
            onSelect={onClose}
            data-dtm={ANALYTICS_KEY}>
            {count == 0 ? (
              <Translate value={'instructions.continue.v3'} />
            ) : (
              <Translate value={'continue.current.build.v3'} />
            )}
          </ModalFooterPrimaryButton>
        </ModalFooterButtons>
      </ModalFooter>
    </Modal>
  );
};

export default withTranslate(withSaveBuild(SavedBuilds));
